// --- Our Modules --------------------------------------------------------------------------------
import config from "@/config/constants";
import type { Lang } from "@/hooks/useLang/types";
import type { TyLang } from "@/types";

// --- Types --------------------------------------------------------------------------------------

export type TyLangValues = Partial<Record<Lang, string>>;

// --- Logic --------------------------------------------------------------------------------------

/** Explodes the API's `[{ lang, value }]` shape into the flat map every form field binds to. */
export const readLangs = (entries: TyLang[] | undefined, languages: Lang[]): TyLangValues => {
    const values: TyLangValues = {};
    for (const lang of languages) values[lang] = entries?.find((entry) => entry.lang === lang)?.value ?? "";
    return values;
};

/**
 * Rebuilds the API shape from the form map.
 *
 * Empty languages are dropped rather than stored blank, so a store that adds a language later gets a
 * field the translation job can recognise as missing instead of one that merely looks filled.
 *
 * `default` follows the store's own `defaultLang` — never a hardcoded language. When the default was
 * left empty the first language that was actually filled takes the flag, because `transStr` on the
 * backend falls back to it and a record with no default at all would resolve to whatever sorted first.
 */
export const writeLangs = (values: TyLangValues, languages: Lang[], defaultLang: Lang): TyLang[] => {
    const filled = languages.filter((lang) => (values[lang] ?? "").trim().length > 0);
    if (filled.length === 0) return [];

    const flagged = filled.includes(defaultLang) ? defaultLang : filled[0];
    return filled.map((lang) => ({ lang, value: (values[lang] ?? "").trim(), default: lang === flagged }));
};

/**
 * One language is enough for a required field. Deliberately not surfaced to the client as a rule —
 * they simply find the form submits — because every read path already falls back across languages.
 */
export const hasAnyLangValue = (values: TyLangValues): boolean => Object.values(values).some((value) => (value ?? "").trim().length > 0);

/**
 * One value for display: the caller's preferred language, else the first language the record actually
 * has. Mirrors the backend's `transStr`, so a half-filled record reads the same on both sides.
 */
export const pickLangValue = (values: TyLangValues, preferred: Lang): string => {
    const direct = (values[preferred] ?? "").trim();
    if (direct) return direct;
    for (const lang of config.menuLanguages) {
        const value = (values[lang] ?? "").trim();
        if (value) return value;
    }
    return "";
};

/** Canonical order, invalid entries dropped. Mirrors what the API stores. */
export const orderLangs = (languages: readonly string[] | undefined): Lang[] =>
    config.menuLanguages.filter((lang) => languages?.includes(lang)) as Lang[];

export const isRtlLang = (lang: Lang): boolean => (config.rtlLanguages as readonly string[]).includes(lang);
